<template>
  <footer class="site-footer" aria-labelledby="footer-brand">
    <div class="site-footer__inner">
      <div class="site-footer__brand">
        <router-link to="/" class="site-footer__mark" aria-label="Heaven's Fruit — inicio">
          <img
            src="@/assets/images/logo.png"
            alt=""
            width="92"
            height="92"
            class="site-footer__logo"
            loading="lazy"
            decoding="async"
          >
        </router-link>
      </div>

      <nav class="site-footer__nav" aria-label="Navegación del pie de página">
        <router-link to="/">Inicio</router-link>
        <router-link to="/productos/">Catálogo</router-link>
        <router-link to="/galeria">Galería</router-link>
        <router-link to="/blog">Blog</router-link>
      </nav>

      <div class="site-footer__social">
        <p class="site-footer__eyebrow">Seguinos</p>
        <a
          :href="siteSettings.instagram_url"
          target="_blank"
          rel="noopener noreferrer"
          class="social-link"
          aria-label="Heaven's Fruit en Instagram (se abre en una pestaña nueva)"
        >
          <svg width="19" height="19" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path fill="currentColor" d="M7.8 2h8.4C19.4 2 22 4.6 22 7.8v8.4A5.8 5.8 0 0 1 16.2 22H7.8C4.6 22 2 19.4 2 16.2V7.8A5.8 5.8 0 0 1 7.8 2m-.2 2A3.58 3.58 0 0 0 4 7.6v8.8A3.58 3.58 0 0 0 7.6 20h8.8A3.58 3.58 0 0 0 20 16.4V7.6A3.58 3.58 0 0 0 16.4 4H7.6zM12 7a5 5 0 0 1 5 5 5 5 0 0 1-5 5 5 5 0 0 1-5-5 5 5 0 0 1 5-5m0 2a3 3 0 0 0-3 3 3 3 0 0 0 3 3 3 3 0 0 0 3-3 3 3 0 0 0-3-3m5-1a1 1 0 0 1 1 1 1 1 0 0 1-1 1 1 1 0 0 1-1-1 1 1 0 0 1 1-1z"/>
          </svg>
          <span>{{ siteSettings.instagram_handle }}</span>
        </a>
      </div>
    </div>

    <div class="site-footer__bottom">
      <p>© {{ currentYear }} Heaven's Fruit. Todos los derechos reservados.</p>
      <a
        href="https://permaculture.ar"
        target="_blank"
        rel="noopener noreferrer"
        class="developer-credit"
        aria-label="Desarrollado por Permaculture (se abre en una pestaña nueva)"
      >
        <span>Desarrollado por</span>
        <img
          src="@/assets/images/logo-star-white.svg"
          alt="Permaculture"
          width="113"
          height="20"
          loading="lazy"
          decoding="async"
        >
      </a>
    </div>
  </footer>
</template>

<script setup>
import { computed, onMounted } from 'vue'
import { useSiteSettings, loadSiteSettings } from '@/services/settings'

const siteSettings = useSiteSettings()
const currentYear = computed(() => new Date().getFullYear())

onMounted(() => {
  loadSiteSettings()
})
</script>

<style scoped>
.site-footer {
  position: relative;
  flex-shrink: 0;
  /* Mismo contenedor que el navbar: el contenido llega a los mismos extremos */
  padding: clamp(3.5rem, 7vw, 6rem) 1rem 1.5rem;
  background: var(--background, #000);
  border-top: 1px solid var(--border-subtle, rgba(255, 255, 255, 0.1));
}

.site-footer__inner {
  max-width: var(--container-width, 1200px);
  margin-inline: auto;
  display: grid;
  grid-template-columns: minmax(0, 1.5fr) minmax(9rem, 1fr) minmax(11rem, 1fr);
  gap: clamp(2rem, 5vw, 4.5rem);
  align-items: center;
  padding-bottom: clamp(2.5rem, 5vw, 4rem);
}

.site-footer__brand {
  display: flex;
  gap: 1.5rem;
  flex-direction: column;
  max-width: 36rem;
}

.site-footer__mark {
  flex: 0 0 auto;
  color: inherit;
}

.site-footer__logo {
  display: block;
  width: 302px;
  height: auto;
  opacity: 0.92;
  transition: opacity var(--duration-fast, 250ms) var(--ease-smooth-out, cubic-bezier(0.22, 1, 0.36, 1));
}

.site-footer__mark:hover .site-footer__logo,
.site-footer__mark:focus-visible .site-footer__logo {
  opacity: 1;
}

.site-footer__eyebrow {
  margin: 0 0 0.8rem;
  color: var(--text-primary, #fff);
  font-size: 0.62rem;
  font-weight: 600;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}

.site-footer__description {
  margin: 0;
  color: var(--text-secondary, #c8c8c8);
  font-size: 0.9rem;
  line-height: 1.7;
  max-width: 30rem;
}

.site-footer__nav {
  display: grid;
  gap: 0.7rem;
}

.site-footer__nav a,
.social-link {
  font-size: 0.82rem;
  letter-spacing: 0.01em;
}

.site-footer__nav a,
.social-link,
.developer-credit {
  width: fit-content;
  color: var(--text-secondary, #c8c8c8);
  text-decoration: none;
  transition:
    color var(--duration-fast, 250ms) var(--ease-smooth-out, cubic-bezier(0.22, 1, 0.36, 1)),
    opacity var(--duration-fast, 250ms) var(--ease-smooth-out, cubic-bezier(0.22, 1, 0.36, 1)),
    transform var(--duration-fast, 250ms) var(--ease-smooth-out, cubic-bezier(0.22, 1, 0.36, 1));
}

.site-footer__nav a:hover,
.site-footer__nav a:focus-visible,
.social-link:hover,
.social-link:focus-visible,
.developer-credit:hover,
.developer-credit:focus-visible {
  color: var(--text-primary, #fff);
  transform: translateY(-2px);
}

.site-footer__nav a.router-link-exact-active {
  color: var(--text-primary, #fff);
}

.site-footer__social {
  display: grid;
}

.social-link {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  min-height: 44px;
}

.site-footer__bottom {
  max-width: var(--container-width, 1200px);
  margin-inline: auto;
  display: flex;
  justify-content: space-between;
  gap: 2rem;
  align-items: center;
  padding-top: 1.25rem;
  border-top: 1px solid var(--border-subtle, rgba(255, 255, 255, 0.1));
  color: var(--text-muted, #8d8d8d);
  font-size: 0.72rem;
}

.site-footer__bottom p {
  margin: 0;
  /* el <p> global trae 1rem: acá tiene que heredar el tamaño de la barra */
  font-size: inherit;
  color: inherit;
}

.developer-credit {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  font-size: 0.72rem;
}

.developer-credit img {
  width: auto;
  height: 20px;
  margin-bottom: -8px;
  opacity: 0.78;
}

@media (max-width: 820px) {
  .site-footer__inner {
    grid-template-columns: 1fr 1fr;
  }

  .site-footer__brand {
    grid-column: 1 / -1;
  }
}

@media (max-width: 560px) {
  .site-footer { padding-inline: 1.25rem; }
  .site-footer__inner {
    grid-template-columns: 1fr;
    gap: 2.5rem;
    text-align: center;
    justify-items: center;
  }

  .site-footer__brand {
    grid-column: auto;
    align-items: center;
  }

  .site-footer__bottom {
    align-items: center;
    flex-direction: column;
    text-align: center;
  }

  .site-footer__nav, .site-footer__social { justify-items: center; }
  .site-footer__nav a, .social-link, .developer-credit { margin-inline: auto; }
}

@media (prefers-reduced-motion: reduce) {
  .site-footer__logo,
  .site-footer__nav a,
  .social-link,
  .developer-credit {
    transition: none !important;
  }
}
</style>
